import React, { useCallback, useEffect, useState } from 'react';
import { Banner, Button, Card, EmptyState, Select, Spinner, Switch, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { useProperty } from '../../app/PropertyContext';
import { settingsApi } from './settingsApi';

/**
 * Receiving rules: organization default plus optional per-property override.
 * Currently one rule — mandatory photo evidence per received line.
 */
export default function ReceivingSettingsTab() {
  const toast = useToast();
  const t = useT();
  const { properties, loaded: propsLoaded } = useProperty();

  const [orgRequire, setOrgRequire] = useState(true);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgSaving, setOrgSaving] = useState(false);

  const [selectedProperty, setSelectedProperty] = useState('');
  const [propOverride, setPropOverride] = useState(false);
  const [propRequire, setPropRequire] = useState(true);
  const [propLoading, setPropLoading] = useState(false);
  const [propSaving, setPropSaving] = useState(false);
  const [propLoaded, setPropLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const org = await settingsApi.getOrg();
        setOrgRequire(org?.requireLinePhotosOnReceive !== false);
      } catch (err) {
        toast.error(t('Could not load settings'), { description: err?.message });
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [toast, t]);

  const loadProperty = useCallback(async (propertyId) => {
    setSelectedProperty(propertyId);
    setPropLoaded(false);
    if (!propertyId) return;
    setPropLoading(true);
    try {
      const doc = await settingsApi.getPropertyCommercial(propertyId);
      setPropOverride(!!doc?.overrideReceiving);
      setPropRequire(doc?.requireLinePhotosOnReceive !== false);
      setPropLoaded(true);
    } catch (err) {
      toast.error(t('Could not load property settings'), { description: err?.message });
    } finally {
      setPropLoading(false);
    }
  }, [toast, t]);

  const saveOrg = async () => {
    setOrgSaving(true);
    try {
      const res = await settingsApi.updateOrgReceiving({ requireLinePhotosOnReceive: orgRequire });
      setOrgRequire(res?.requireLinePhotosOnReceive !== false);
      toast.success(t('Receiving rules saved'));
    } catch (err) {
      toast.error(t('Could not save settings'), { description: err?.message });
    } finally {
      setOrgSaving(false);
    }
  };

  const saveProperty = async () => {
    if (!selectedProperty) return;
    setPropSaving(true);
    try {
      const res = await settingsApi.updatePropertyReceiving(selectedProperty, {
        propertyId: selectedProperty,
        overrideReceiving: propOverride,
        requireLinePhotosOnReceive: propRequire,
      });
      setPropOverride(!!res?.overrideReceiving);
      setPropRequire(res?.requireLinePhotosOnReceive !== false);
      toast.success(t('Property overrides saved'));
    } catch (err) {
      toast.error(t('Could not save overrides'), { description: err?.message });
    } finally {
      setPropSaving(false);
    }
  };

  if (orgLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <section>
        <h2 className="appr-scope__title">{t('Organization defaults')}</h2>
        <Card>
          <Card.Body>
            <div className="set-section__head">
              <div>
                <h3 className="set-section__title">{t('Photo evidence on receiving')}</h3>
                <p className="set-section__hint">
                  {t('When on, the store keeper must attach a photo to every line with an accepted or rejected quantity before a goods receipt can be posted. Rejections get documented and deliveries are verifiable.')}
                </p>
              </div>
              <Switch
                checked={orgRequire}
                onChange={setOrgRequire}
                label={t('Require a photo for every received line')}
              />
            </div>
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Button variant="primary" size="sm" loading={orgSaving} onClick={saveOrg}>{t('Save')}</Button>
            </div>
          </Card.Body>
        </Card>
      </section>

      <section>
        <h2 className="appr-scope__title">{t('Property overrides')}</h2>
        {!propsLoaded ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
        ) : properties.length === 0 ? (
          <EmptyState
            icon={<Icon.Building size={28} />}
            title={t('No properties yet')}
            description={t('Once a property exists, you can override the organization receiving rules for it.')}
          />
        ) : (
          <>
            <div className="set-property-pick">
              <Select
                options={[{ value: '', label: t('Select a property…') }, ...properties.map((p) => ({ value: p.id, label: p.name }))]}
                value={selectedProperty}
                onChange={loadProperty}
                ariaLabel={t('Property')}
                searchable
              />
            </div>

            {!selectedProperty ? (
              <Banner tone="info">{t('Choose a property to override its receiving rules. Left as inherit, it uses the organization defaults.')}</Banner>
            ) : propLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>
            ) : propLoaded ? (
              <Card>
                <Card.Body>
                  <div className="set-section__head">
                    <div>
                      <h3 className="set-section__title">{t('Property override')}</h3>
                      <p className="set-section__hint">{t('When off, this property inherits the organization receiving rules.')}</p>
                    </div>
                    <Switch checked={propOverride} onChange={setPropOverride} label={t('Override')} />
                  </div>
                  {propOverride ? (
                    <div style={{ marginTop: 'var(--space-4)' }}>
                      <Switch
                        checked={propRequire}
                        onChange={setPropRequire}
                        label={t('Require a photo for every received line')}
                      />
                    </div>
                  ) : (
                    <p className="set-inherit" style={{ marginTop: 'var(--space-4)' }}>
                      {t('Inherits the organization receiving rules.')}
                    </p>
                  )}
                  <div style={{ marginTop: 'var(--space-4)' }}>
                    <Button variant="primary" size="sm" loading={propSaving} onClick={saveProperty}>{t('Save')}</Button>
                  </div>
                </Card.Body>
              </Card>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
